<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('suppliers')"
      :subtitle="$t('manage_suppliers')"
      icon="mdi-truck-delivery"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <global-button-print @click="printSuppliers" />
        <primary-button @click="addSupplier">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_supplier") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar
      v-if="!device.isMobile"
      :search.sync="searchSupplier"
      :search-placeholder="$t('search_suppliers_placeholder')"
      searchable
      :loading="isLoading"
    />

    <warehouse-supplier-dialog
      v-model="supplierDialog"
      :edited-supplier="editedSupplier"
      @saved="fetchSuppliers"
    />

    <!-- Empty state -->
    <div v-if="!isLoading && suppliers.length === 0" class="px-6 mt-2">
      <v-card outlined>
        <v-card-text class="text-center py-12">
          <v-icon size="80" color="grey lighten-1" class="mb-4">
            mdi-truck-delivery-outline
          </v-icon>
          <h3 class="grey--text text--darken-1 mb-2">
            {{ $t("no_suppliers") }}
          </h3>
          <p class="grey--text">
            {{ $t("no_suppliers_description") }}
          </p>
          <primary-button class="mt-4" @click="addSupplier">
            <v-icon class="mr-2">mdi-plus-circle</v-icon>
            {{ $t("add_supplier") }}
          </primary-button>
        </v-card-text>
      </v-card>
    </div>

    <!-- Desktop View -->
    <template v-else-if="!device.isMobile">
      <div class="px-6 mt-2">
        <v-data-table
          :headers="headers"
          :items="filteredSuppliers"
          :loading="isLoading"
          :loading-text="$t('loading_data')"
          class="striped-table ordera-box-shadow elevation-1"
        >
          <template v-slot:item.id="{ item }">
            <span class="font-weight-medium">{{ item.id }}</span>
          </template>
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center py-2">
              <v-avatar color="primary" size="36" class="mr-3">
                <v-icon color="white" size="20">mdi-truck-delivery</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div v-if="item.jib || item.pib" class="text-caption grey--text">
                  <span v-if="item.jib">JIB: {{ item.jib }}</span>
                  <span v-if="item.jib && item.pib" class="mx-1">•</span>
                  <span v-if="item.pib">PIB: {{ item.pib }}</span>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:item.contact="{ item }">
            <div v-if="item.contact">
              <v-icon small class="mr-1" color="grey darken-1">mdi-account</v-icon>
              <span>{{ item.contact }}</span>
            </div>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.phone="{ item }">
            <div v-if="item.phone" class="d-flex align-center">
              <v-icon small class="mr-1" color="grey darken-1">mdi-phone</v-icon>
              <a :href="`tel:${item.phone}`" class="text-decoration-none black--text">{{
                item.phone
              }}</a>
            </div>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.email="{ item }">
            <div v-if="item.email" class="d-flex align-center">
              <v-icon small class="mr-1" color="grey darken-1">mdi-email</v-icon>
              <a
                :href="`mailto:${item.email}`"
                class="text-decoration-none black--text"
                >{{ item.email }}</a
              >
            </div>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.location="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.city }}</div>
              <div class="text-caption grey--text">{{ item.address }}</div>
            </div>
          </template>
          <template v-slot:item.note="{ item }">
            <div v-if="item.note" class="text-truncate" style="max-width: 200px">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">{{ item.note }}</span>
                </template>
                <span>{{ item.note }}</span>
              </v-tooltip>
            </div>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <edit-button class="mr-2" @click="editSupplier(item)"></edit-button>
            <remove-button @click="deleteSupplier(item)"></remove-button>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <div class="px-3 mt-3">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="filteredSuppliers"
          :loading="isLoading"
          item-key="id"
          show-filters
          :show-row-number="false"
          :fields-per-row="2"
          :bold-fields="['name']"
        >
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center">
              <v-avatar color="primary" size="24" class="mr-2">
                <v-icon color="white" size="14">mdi-truck-delivery</v-icon>
              </v-avatar>
              {{ item.name }}
            </div>
          </template>
          <template v-slot:item.phone="{ item }">
            <div v-if="item.phone">
              <a :href="`tel:${item.phone}`" class="text-decoration-none black--text">{{
                item.phone
              }}</a>
            </div>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.city="{ item }">
            {{ item.city || "-" }}
          </template>
          <template v-slot:item.actions="{ item }">
            <edit-button class="mr-2" @click="editSupplier(item)"></edit-button>
            <remove-button @click="deleteSupplier(item)"></remove-button>
          </template>
        </mobile-data-cards>
      </div>

      <div class="fixed-bottom-button">
        <v-btn color="primary" block @click="addSupplier">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_supplier") }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import WarehouseSupplierDialog from "@/components/warehouse/dialogs/WarehouseSupplierDialog.vue";
import warehouseService from "@/services/warehouse";
import GlobalButtonPrint from "@/components/global/buttons/GlobalButtonPrint.vue";
import hasPrinting from "@/mixins/hasPrinting";

export default {
  components: {
    PageHeader,
    PageToolbar,
    MobileDataCards,
    WarehouseSupplierDialog,
    GlobalButtonPrint,
  },
  mixins: [hasPrinting],
  data: () => ({
    searchSupplier: "",
    isLoading: false,
    suppliers: [],
    supplierDialog: false,
    editedSupplier: null,
  }),
  computed: {
    headers() {
      return [
        { text: "ID", value: "id", align: "start", width: "70px" },
        { text: this.$t("title"), value: "name", align: "start", width: "250px" },
        {
          text: this.$t("contact_person"),
          value: "contact",
          align: "start",
          width: "180px",
        },
        { text: this.$t("phone_no"), value: "phone", align: "start", width: "150px" },
        { text: this.$t("email"), value: "email", align: "start", width: "200px" },
        { text: this.$t("location"), value: "location", align: "start", width: "220px" },
        { text: this.$t("note"), value: "note", align: "start", width: "200px" },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "center",
          sortable: false,
          width: "120px",
          print: false,
        },
      ];
    },
    filteredSuppliers() {
      if (!this.searchSupplier) return this.suppliers;

      const search = this.searchSupplier.toLowerCase();
      return this.suppliers.filter(
        (supplier) =>
          supplier.name?.toLowerCase().includes(search) ||
          supplier.city?.toLowerCase().includes(search) ||
          supplier.address?.toLowerCase().includes(search) ||
          supplier.contact?.toLowerCase().includes(search) ||
          supplier.phone?.toLowerCase().includes(search) ||
          supplier.email?.toLowerCase().includes(search)
      );
    },
    mobileHeaders() {
      return [
        { text: this.$t("title"), value: "name", cols: 6 },
        { text: this.$t("phone_no"), value: "phone", cols: 6 },
        { text: this.$t("city"), value: "city", cols: 6 },
        { text: this.$t("actions"), value: "actions", cols: 6 },
      ];
    },
  },
  watch: {
    supplierDialog(val) {
      if (!val) {
        this.editedSupplier = null;
      }
    },
  },
  created() {
    this.fetchSuppliers();
  },
  methods: {
    addSupplier() {
      this.editedSupplier = null;
      this.supplierDialog = true;
    },
    async fetchSuppliers() {
      this.isLoading = true;
      try {
        const { data } = await warehouseService.getSuppliers();
        this.suppliers = data;
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      } finally {
        this.isLoading = false;
      }
    },
    editSupplier(supplier) {
      this.editedSupplier = { ...supplier };
      this.supplierDialog = true;
    },
    deleteSupplier(supplier) {
      this.$refs.confirmBox
        .open(this.$t("delete_confirmation"), this.$t("delete_supplier_confirmation"), {
          color: "error",
        })
        .then(() => {
          warehouseService
            .deleteSupplier(supplier.id)
            .then(() => {
              const index = this.suppliers.findIndex((s) => s.id === supplier.id);
              if (index !== -1) {
                this.suppliers.splice(index, 1);
              }
              this.$store.commit("snackbar/showMessage", {
                message: this.$t("supplier_deleted_success"),
                color: "success",
              });
            })
            .catch(() => {
              this.$store.commit("snackbar/showMessage", {
                message: this.$t("supplier_deleted_error"),
                color: "error",
              });
            });
        })
        .catch(() => {
          // User cancelled
        });
    },
    printSuppliers() {
      const printHeaders = this.headers.filter((h) => h.print !== false);
      const printItems = this.filteredSuppliers.map((supplier) => {
        return {
          id: supplier.id,
          name: supplier.name || "-",
          contact: supplier.contact || "-",
          phone: supplier.phone || "-",
          email: supplier.email || "-",
          location: `${supplier.city || "-"}, ${supplier.address || "-"}`,
          note: supplier.note || "-",
        };
      });

      this.printDocument({
        pageTitle: this.$t("suppliers"),
        title: this.$t("suppliers"),
        tables: [
          {
            headers: printHeaders,
            items: printItems,
            showCaption: true,
          },
        ],
        hideDatetime: false,
      });
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
